'use client'

import React, { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { MultipleRadio, useHog } from '@microfrontends/react-components'
import { fireGlobalSearchEvent } from '../../../SearchResults/helpers/filterHelper'
import { TRootState } from '../../../SearchResults/searchResultsRootReducers'
import { useExperiment8Freshness } from '../../context/Experiment8FreshnessContext'
import {
    buildExperiment8ExtraFilterSearchProps,
    syncExperiment8ExtraFilterCookies
} from '../../helpers/experiment8ExtraFilterTracking'
import { ISearchFilterParams } from '../../../SearchResults/types/ISearchFilterParams'
import { useJobSearchContext } from '../../context'
import { TTranslation } from '../../translations'
import { ELanguage } from '../../../SearchResults/utils/language'
import { SortBarExtraFilterRowsRoot } from './SortBarExtraFilterRows.styles'

const POSTED_OPTIONS: { id: '24h' | '3d' | '7d'; labelKey: TTranslation }[] = [
    { id: '24h', labelKey: 'Within 24 hours' },
    { id: '3d', labelKey: 'Within 3 days' },
    { id: '7d', labelKey: 'Within 7 days' }
]

const APPLICANT_OPTIONS: { id: 'lt5' | 'lt20'; labelKey: TTranslation; apiValue: '5' | '20' }[] = [
    { id: 'lt5', labelKey: 'Fewer than 5 applicants', apiValue: '5' },
    { id: 'lt20', labelKey: 'Fewer than 20 applicants', apiValue: '20' }
]
const ALL_QUICK_FILTER_VALUE = -1

export type TSortBarExtraFilterRowsProps = {
    variant?: 'desktop' | 'compact'
    embedded?: boolean
    translate?: (term: string) => string
    language?: ELanguage
    onQuickDropdownAction?: () => void
    forceDropdown?: boolean
    inlineDropdowns?: boolean
    hideInlineClearButton?: boolean
    showInlineClearButton?: boolean
    onInlineClearAllFilters?: () => void
}

function SortBarExtraFilterRowsInner({
    variant = 'desktop',
    embedded = false,
    onQuickDropdownAction,
    forceDropdown = false,
    inlineDropdowns = false,
    hideInlineClearButton = false,
    showInlineClearButton = false,
    onInlineClearAllFilters,
    t,
    language
}: TSortBarExtraFilterRowsProps & { t: (key: TTranslation) => string; language: ELanguage }) {
    const lastParams = useSelector((state: TRootState) => state.searchFilter.lastParams)
    const { isVersionA, isExperiment8Ready } = useExperiment8Freshness()
    const { tracking } = useHog()

    const isCompact = variant === 'compact'
    const isDropdownMode = isCompact || forceDropdown
    const hideInlineLabel = (isCompact && embedded) || inlineDropdowns
    const cookieScope: 'desktop' | 'mobile' = isCompact ? 'mobile' : 'desktop'

    const postedSelected =
        lastParams.postedWithin === '24h' || lastParams.postedWithin === '3d' || lastParams.postedWithin === '7d'
            ? lastParams.postedWithin
            : null

    const applicantChipSelected =
        lastParams.applicantsLessThan === '5'
            ? 'lt5'
            : lastParams.applicantsLessThan === '20'
              ? 'lt20'
              : null

    const trackExperiment8FilterSearch = useCallback(
        (next: { postedWithin: typeof lastParams.postedWithin; applicantsLessThan: typeof lastParams.applicantsLessThan }) => {
            if (!isVersionA || !isExperiment8Ready) {
                return
            }
            const experiment8Properties = buildExperiment8ExtraFilterSearchProps(next)
            const properties = {
                jobFunctionId: lastParams.jobFunctionId,
                companyIndustryId: lastParams.companyIndustries,
                jobLevelId: lastParams.jobLevelId,
                workingTypeId: lastParams.typeWorkingId,
                salaryRange: lastParams.salary,
                isUrgent: lastParams.isUrgentJob,
                keyword: lastParams.query,
                locationId: lastParams.locationId,
                districtId: lastParams.districtId,
                ...experiment8Properties
            }
            const hadPosthogCapture = Object.keys(experiment8Properties).length > 0
            if (hadPosthogCapture) {
                tracking({
                    eventName: 'js_search_result_page:filter_search_result',
                    properties
                })
                console.log('[js_search_result_page:filter_search_result] experiment8 extra filters (PostHog)', properties)
            }
            syncExperiment8ExtraFilterCookies(next, hadPosthogCapture, cookieScope)
        },
        [cookieScope, isVersionA, isExperiment8Ready, lastParams, tracking]
    )

    const setPosted = (id: (typeof POSTED_OPTIONS)[number]['id']) => {
        const clearing = postedSelected === id
        const nextPosted = (clearing ? '' : id) as typeof lastParams.postedWithin
        const nextPostedPayload = clearing ? '' : id
        const nextApplicants =
            lastParams.applicantsLessThan === '5' || lastParams.applicantsLessThan === '20'
                ? lastParams.applicantsLessThan
                : ('' as const)

        fireSearchWithoutJumpTop({ postedWithin: nextPostedPayload, page: 0 }, 'SortBarExtraFilterRows:postedWithin')
        trackExperiment8FilterSearch({ postedWithin: nextPosted, applicantsLessThan: nextApplicants })
        onQuickDropdownAction?.()
    }

    const setApplicants = (chipId: (typeof APPLICANT_OPTIONS)[number]['id'], apiValue: '5' | '20') => {
        const clearing = applicantChipSelected === chipId
        const nextApplicants = (clearing ? '' : apiValue) as typeof lastParams.applicantsLessThan
        const nextPosted =
            lastParams.postedWithin === '24h' || lastParams.postedWithin === '3d' || lastParams.postedWithin === '7d'
                ? lastParams.postedWithin
                : ('' as const)

        fireSearchWithoutJumpTop(
            { applicantsLessThan: clearing ? '' : apiValue, page: 0 },
            'SortBarExtraFilterRows:applicantsLessThan'
        )
        trackExperiment8FilterSearch({ postedWithin: nextPosted, applicantsLessThan: nextApplicants })
        onQuickDropdownAction?.()
    }

    const clearPosted = () => {
        const nextApplicants =
            lastParams.applicantsLessThan === '5' || lastParams.applicantsLessThan === '20'
                ? lastParams.applicantsLessThan
                : ('' as const)
        fireSearchWithoutJumpTop({ postedWithin: '', page: 0 }, 'SortBarExtraFilterRows:postedWithin')
        trackExperiment8FilterSearch({ postedWithin: '' as typeof lastParams.postedWithin, applicantsLessThan: nextApplicants })
        onQuickDropdownAction?.()
    }

    const clearApplicants = () => {
        const nextPosted =
            lastParams.postedWithin === '24h' || lastParams.postedWithin === '3d' || lastParams.postedWithin === '7d'
                ? lastParams.postedWithin
                : ('' as const)
        fireSearchWithoutJumpTop({ applicantsLessThan: '', page: 0 }, 'SortBarExtraFilterRows:applicantsLessThan')
        trackExperiment8FilterSearch({ postedWithin: nextPosted, applicantsLessThan: '' as typeof lastParams.applicantsLessThan })
        onQuickDropdownAction?.()
    }

    const clearAllLabel = language === ELanguage.VI ? 'Xoá bộ lọc' : 'Clear filters'
    const quickFilterDropdownWidth = language === ELanguage.VI ? '250px' : '280px'
    const hasAnyInlineSelection = Boolean(postedSelected || applicantChipSelected)
    const shouldShowInlineClearButton = onInlineClearAllFilters ? showInlineClearButton : hasAnyInlineSelection
    const allPostedOptionLabel = language === ELanguage.VI ? 'Tất cả ngày đăng' : 'All posted dates'
    const allApplicantsOptionLabel = language === ELanguage.VI ? 'Tất cả ứng viên' : 'All applicants'
    const postedDropdownOptions = useMemo(
        () => [
            { id: ALL_QUICK_FILTER_VALUE, name: allPostedOptionLabel },
            ...POSTED_OPTIONS.map((opt) => ({ id: opt.id, name: t(opt.labelKey) }))
        ],
        [allPostedOptionLabel, t]
    )
    const applicantDropdownOptions = useMemo(
        () => [
            { id: ALL_QUICK_FILTER_VALUE, name: allApplicantsOptionLabel },
            ...APPLICANT_OPTIONS.map((opt) => ({ id: opt.id, name: t(opt.labelKey) }))
        ],
        [allApplicantsOptionLabel, t]
    )

    const fireSearchWithoutJumpTop = (params: Partial<ISearchFilterParams>, from: string) => {
        const shouldPreserveScroll = inlineDropdowns && typeof window !== 'undefined'
        const currentScrollY = shouldPreserveScroll ? window.scrollY : 0
        fireGlobalSearchEvent(params, from, false)
        if (shouldPreserveScroll) {
            window.requestAnimationFrame(() => {
                window.scrollTo(0, currentScrollY)
            })
            window.setTimeout(() => {
                window.scrollTo(0, currentScrollY)
            }, 0)
        }
    }

    const clearAllExtraFilters = () => {
        if (onInlineClearAllFilters) {
            onInlineClearAllFilters()
            onQuickDropdownAction?.()
            return
        }
        if (!hasAnyInlineSelection) {
            return
        }
        fireSearchWithoutJumpTop(
            {
                postedWithin: '',
                applicantsLessThan: '',
                page: 0
            },
            'SortBarExtraFilterRows:clearAll'
        )
        trackExperiment8FilterSearch({
            postedWithin: '' as typeof lastParams.postedWithin,
            applicantsLessThan: '' as typeof lastParams.applicantsLessThan
        })
        onQuickDropdownAction?.()
    }

    return (
        <SortBarExtraFilterRowsRoot
            $isCompact={isCompact}
            $embedded={embedded}
            $language={language}
            $inlineDropdowns={inlineDropdowns}
            data-testid='sort-bar-extra-filters'
        >
            <div className='sbeef__inner'>
                <div className='sbeef__row'>
                    {!hideInlineLabel ? <span className='sbeef__label'>{t('Job newly posted')}</span> : null}
                    {isDropdownMode ? (
                        <div className='sbeef__dropdown'>
                            <MultipleRadio
                                name='sort-bar-posted-within'
                                value={postedSelected ?? ALL_QUICK_FILTER_VALUE}
                                options={postedDropdownOptions}
                                onChange={(response) => {
                                    const selectedValue = response.value as typeof ALL_QUICK_FILTER_VALUE | '24h' | '3d' | '7d'
                                    if (selectedValue === ALL_QUICK_FILTER_VALUE) {
                                        clearPosted()
                                        return
                                    }
                                    if (selectedValue) {
                                        setPosted(selectedValue)
                                    } else {
                                        clearPosted()
                                    }
                                }}
                                dropdown={{
                                    width: quickFilterDropdownWidth,
                                    floatingConfig: {
                                        position: 'fixed',
                                        enablePortal: true,
                                        useWindowAsFlippingContainer: true
                                    }
                                }}
                                dropdownStyle={{ zIndex: 1101 }}
                                placement='bottom-start'
                                selectLayout={{ type: isCompact ? 'responsive' : 'highlight' }}
                                notCountedAsValue={[ALL_QUICK_FILTER_VALUE]}
                                placeholder={t('Job newly posted')}
                                placeholderColor='#333'
                            />
                        </div>
                    ) : (
                        <div className='sbeef__chips'>
                            {POSTED_OPTIONS.map((opt) => (
                                <button
                                    key={opt.id}
                                    type='button'
                                    className={`sbeef__chip${postedSelected === opt.id ? ' sbeef__chip--active' : ''}`}
                                    onClick={() => setPosted(opt.id)}
                                >
                                    {t(opt.labelKey)}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div className='sbeef__row'>
                    {!hideInlineLabel ? <span className='sbeef__label'>{t('Number of applicants')}</span> : null}
                    {isDropdownMode ? (
                        <div className='sbeef__dropdown'>
                            <MultipleRadio
                                name='sort-bar-applicants-less-than'
                                value={applicantChipSelected ?? ALL_QUICK_FILTER_VALUE}
                                options={applicantDropdownOptions}
                                onChange={(response) => {
                                    const selectedValue = response.value as typeof ALL_QUICK_FILTER_VALUE | 'lt5' | 'lt20'
                                    if (selectedValue === ALL_QUICK_FILTER_VALUE) {
                                        clearApplicants()
                                        return
                                    }
                                    if (selectedValue === 'lt5') {
                                        setApplicants('lt5', '5')
                                    } else if (selectedValue === 'lt20') {
                                        setApplicants('lt20', '20')
                                    } else {
                                        clearApplicants()
                                    }
                                }}
                                dropdown={{
                                    width: quickFilterDropdownWidth,
                                    floatingConfig: {
                                        position: 'fixed',
                                        enablePortal: true,
                                        useWindowAsFlippingContainer: true
                                    }
                                }}
                                dropdownStyle={{ zIndex: 1101 }}
                                placement='bottom-start'
                                selectLayout={{ type: isCompact ? 'responsive' : 'highlight' }}
                                notCountedAsValue={[ALL_QUICK_FILTER_VALUE]}
                                placeholder={t('Number of applicants')}
                                placeholderColor='#333'
                            />
                        </div>
                    ) : (
                        <div className='sbeef__chips'>
                            {APPLICANT_OPTIONS.map((opt) => (
                                <button
                                    key={opt.id}
                                    type='button'
                                    className={`sbeef__chip${applicantChipSelected === opt.id ? ' sbeef__chip--active' : ''}`}
                                    onClick={() => setApplicants(opt.id, opt.apiValue)}
                                >
                                    {t(opt.labelKey)}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                {inlineDropdowns && shouldShowInlineClearButton && !hideInlineClearButton ? (
                    <button
                        type='button'
                        className='sbeef__inline-clear'
                        onClick={clearAllExtraFilters}
                    >
                        {clearAllLabel}
                    </button>
                ) : null}
            </div>
        </SortBarExtraFilterRowsRoot>
    )
}

function SortBarExtraFilterRowsWithJobSearchContext(props: TSortBarExtraFilterRowsProps) {
    const { t, language } = useJobSearchContext()
    return <SortBarExtraFilterRowsInner {...props} t={t} language={language as ELanguage} />
}

export default function SortBarExtraFilterRows(props: TSortBarExtraFilterRowsProps) {
    const { translate, language, ...rest } = props
    if (translate) {
        const t = (key: TTranslation) => translate(key)
        return <SortBarExtraFilterRowsInner {...rest} t={t} language={language ?? ELanguage.VI} />
    }
    return <SortBarExtraFilterRowsWithJobSearchContext {...props} />
}
